import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isDomainEvent, type DomainEventEnvelope } from '@bhops/shared/realtime';
import { useAuthStore } from '@/stores/auth';
import { connectRealtime, disconnectRealtime, onDomainEvent } from './socket';

// Maps a domain event from the backend to the TanStack Query keys that
// should be invalidated. Kept intentionally narrow — each entry is a
// handful of queries at most, so invalidation doesn't stampede the API.
//
// Uses `isDomainEvent` from @bhops/shared/realtime to narrow
// `event.payload` per `eventType` so payload accesses are type-safe
// without `as { robotId }` casts at the call site.

function queriesToInvalidate(event: DomainEventEnvelope): string[][] {
  // Robot lifecycle (Robot itself).
  if (
    isDomainEvent(event, 'RobotCreated') ||
    isDomainEvent(event, 'RobotStatusChanged') ||
    isDomainEvent(event, 'RobotDeleted')
  ) {
    return [['robots'], ['dashboard'], ['robot', event.aggregateId]];
  }

  // Robot transfer — owner entity changes. Affects fleet roster + the
  // robot's detail panel.
  if (isDomainEvent(event, 'RobotTransferred')) {
    return [['robots'], ['dashboard'], ['robot', event.aggregateId]];
  }

  // Deployment lifecycle (Robot's view of it).
  if (isDomainEvent(event, 'RobotDeployed') || isDomainEvent(event, 'RobotReturned')) {
    return [['robots'], ['dashboard'], ['deployments'], ['robot-timeline', event.payload.robotId]];
  }

  // QR sticker lifecycle.
  if (isDomainEvent(event, 'RobotQRIssued') || isDomainEvent(event, 'RobotQRRevoked')) {
    return [
      ['robot-qrs', event.payload.robotId],
      ['robot-timeline', event.payload.robotId],
    ];
  }

  // Customer report inbox.
  if (
    isDomainEvent(event, 'CustomerReportSubmitted') ||
    isDomainEvent(event, 'CustomerReportStatusChanged') ||
    isDomainEvent(event, 'CustomerReportAssigned') ||
    isDomainEvent(event, 'ReportSlaBreached')
  ) {
    return [['reports'], ['report', event.aggregateId]];
  }

  // ─── P3+ realtime catalog expansion ─────────────────────────────
  // 신규로 invalidate 하는 이벤트들. 핵심 가시성 향상:
  //   • 타 사용자가 SO 를 생성/배정/종료/SLA 초과 시 운영자의 inbox·
  //     로봇 디테일·대시보드가 즉시 갱신
  //   • Recall / Low-stock / Warranty 알림이 dashboard 의 fleet
  //     섹션 + parts 페이지에 즉시 반영
  //   • Privacy/FieldVisit/Deal/Contract/Acceptance 도 inbox 갱신
  //
  // 키 컨벤션은 페이지의 `useQuery({ queryKey: [...] })` 첫 token 과
  // 일치. 변경 시 audit/api 폴더 grep 으로 영향처 확인 필수.

  // Service order lifecycle — Inbox / Triage / RobotDetail FieldServiceTab.
  if (
    isDomainEvent(event, 'ServiceOrderCreated') ||
    isDomainEvent(event, 'ServiceOrderClosed') ||
    isDomainEvent(event, 'ServiceOrderAssigned') ||
    isDomainEvent(event, 'ServiceOrderSlaBreached')
  ) {
    return [
      ['service-orders'],
      ['service-order', event.aggregateId],
      ['dashboard'],
      // P0-2 에서 추가된 로봇별 SO 이력. payload 가 robotId 를 노출하지
      // 않아 대신 전체 무효화 (다음 fetch 시 robotId 필터 재적용).
      ['robot-service-orders'],
    ];
  }

  // Contract handoff — 배정 + 24h escalation cron.
  if (isDomainEvent(event, 'RobotAssignedToContract')) {
    return [
      ['assignments'],
      ['dashboard', 'manufacturing'],
      ['contracts'],
      ['contract', event.aggregateId],
      ['robots'],
    ];
  }
  if (isDomainEvent(event, 'ContractAssignmentEscalation')) {
    return [
      ['assignments'],
      ['dashboard', 'manufacturing'],
      ['contracts'],
      ['contract', event.aggregateId],
    ];
  }

  // Parts / Solutions / Defect-LOT (v0.4.0+v0.5.2 알림 catalog).
  if (isDomainEvent(event, 'PartLotDefectSuspected') || isDomainEvent(event, 'PartLotRecalled')) {
    return [
      ['part-lots'],
      ['parts', 'lots'],
      ['part-lot', event.aggregateId],
      // recall 은 PARTS_SWAP SO 를 trigger 하므로 SO 측도 무효화.
      ['service-orders'],
      ['fleet', 'defect-lots'],
      ['fleet', 'parts-summary'],
      ['dashboard'],
    ];
  }
  if (isDomainEvent(event, 'PartCatalogLowStock')) {
    return [
      ['parts', 'catalog'],
      ['part-catalog', event.aggregateId],
      ['fleet', 'parts-summary'],
      ['dashboard'],
    ];
  }
  if (isDomainEvent(event, 'RobotPartWarrantyExpiring')) {
    // aggregateId 는 storeId — 매장 단위 묶음. 대시보드의 warranty
    // KPI + parts 섹션 + 해당 매장 detail (있다면) 무효화.
    return [['fleet', 'parts-summary'], ['dashboard'], ['notifications']];
  }

  // Privacy request inbox — DSAR.
  if (
    isDomainEvent(event, 'PrivacyRequestSubmitted') ||
    isDomainEvent(event, 'PrivacyRequestStatusChanged') ||
    isDomainEvent(event, 'PrivacyRequestAssigned') ||
    isDomainEvent(event, 'PrivacyRequestSlaBreached')
  ) {
    return [['privacy-requests'], ['privacy-request', event.aggregateId]];
  }

  // Field visit scheduling.
  if (isDomainEvent(event, 'FieldVisitScheduled')) {
    return [['field-visits'], ['robot-timeline', event.payload.robotId]];
  }

  // Deal pipeline — Kanban + detail.
  if (
    isDomainEvent(event, 'DealCreated') ||
    isDomainEvent(event, 'DealStageChanged') ||
    isDomainEvent(event, 'DealDeleted')
  ) {
    return [['deals'], ['deal', event.aggregateId], ['dashboard']];
  }

  // Contract lifecycle — list + detail + manufacturing assignment inbox.
  if (
    isDomainEvent(event, 'ContractCreated') ||
    isDomainEvent(event, 'ContractSigned') ||
    isDomainEvent(event, 'ContractTerminated') ||
    isDomainEvent(event, 'ContractExpired') ||
    isDomainEvent(event, 'ContractRenewalAlerted') ||
    isDomainEvent(event, 'ContractDeleted')
  ) {
    return [
      ['contracts'],
      ['contract', event.aggregateId],
      ['dashboard'],
      // 사인 직후 핸드오프 alarm 으로 assignment inbox 갱신.
      ['assignments'],
    ];
  }

  // Acceptance signed — RobotDetail acceptances 섹션 + 자동 생성된 SO.
  if (isDomainEvent(event, 'AcceptanceSigned')) {
    return [['acceptances'], ['acceptance', event.aggregateId], ['service-orders'], ['dashboard']];
  }

  // plan-modusign-style-acceptance §Phase3 — e-Sign 문서 완료 시
  // /sign/documents 리스트 + detail 페이지 + (Phase 4 자동 생성된)
  // RobotAcceptance 도 함께 갱신.
  if (isDomainEvent(event, 'SignatureDocumentCompleted')) {
    return [
      ['signature-documents'],
      ['signature-document', event.aggregateId],
      ['acceptances'],
      ['dashboard'],
    ];
  }

  return [];
}

export function useRealtimeInvalidation(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();

  useEffect(() => {
    if (!accessToken) {
      disconnectRealtime();
      return;
    }
    connectRealtime(accessToken);

    const off = onDomainEvent((event) => {
      for (const key of queriesToInvalidate(event)) {
        if (key[key.length - 1]) {
          qc.invalidateQueries({ queryKey: key });
        }
      }
    });

    return () => {
      off();
    };
  }, [accessToken, qc]);
}
